/**
 * Feel Good Attire global theme engine
 * Scroll reveals, cursor glow, magnetic controls,
 * disclosures, quantities and Theme Editor support.
 */

(() => {
  'use strict';

  const FGA = {
    initializedRoots: new WeakSet(),
    revealObserver: null,
    glowFrame: null,
    mouseX: 0,
    mouseY: 0,
    glowX: 0,
    glowY: 0,
    pageReady: false
  };

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const finePointerQuery = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  /**
   * Utility helpers
   */
  const clamp = (number, minimum, maximum) => {
    return Math.min(Math.max(number, minimum), maximum);
  };

  const announce = (message) => {
    const liveRegion = document.querySelector('[data-fga-live-region]');

    if (!liveRegion) return;

    liveRegion.textContent = '';

    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  };

  /**
   * Page-ready state
   */
  const initializePageReady = () => {
    if (FGA.pageReady) return;

    FGA.pageReady = true;

    const activate = () => {
      document.documentElement.classList.add('fga-page-ready');
      document.body.classList.add('fga-page-loaded');
    };

    if (document.readyState === 'complete') {
      window.requestAnimationFrame(activate);
    } else {
      window.addEventListener('load', activate, {
        once: true
      });
    }
  };

  /**
   * Scroll reveal animations
   */
  const initializeRevealObserver = () => {
    if (FGA.revealObserver) return;

    if (
      reducedMotionQuery.matches ||
      !('IntersectionObserver' in window)
    ) {
      document.querySelectorAll('.fga-reveal').forEach((element) => {
        element.classList.add('is-visible');
      });

      return;
    }

    FGA.revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
      }
    );
  };

  const registerRevealElements = (root = document) => {
    initializeRevealObserver();

    root.querySelectorAll('.fga-reveal').forEach((element, index) => {
      if (element.dataset.fgaRevealRegistered === 'true') return;

      element.dataset.fgaRevealRegistered = 'true';

      const delay = Number.parseInt(
        element.dataset.revealDelay || '',
        10
      );

      if (Number.isFinite(delay)) {
        element.style.setProperty(
          '--fga-reveal-delay',
          `${clamp(delay, 0, 1200)}ms`
        );
      } else if (
        element.parentElement &&
        element.parentElement.children.length <= 12
      ) {
        const siblingIndex = Array.from(
          element.parentElement.children
        ).indexOf(element);

        element.style.setProperty(
          '--fga-reveal-delay',
          `${Math.min(siblingIndex * 65, 390)}ms`
        );
      }

      if (
        reducedMotionQuery.matches ||
        !FGA.revealObserver
      ) {
        element.classList.add('is-visible');
      } else {
        FGA.revealObserver.observe(element);
      }
    });
  };

  /**
   * Gold mouse-follow glow
   */
  const createCursorGlow = () => {
    if (!finePointerQuery.matches || reducedMotionQuery.matches) return;

    if (document.querySelector('[data-fga-cursor-glow]')) return;

    const glow = document.createElement('div');

    glow.className = 'fga-cursor-glow';
    glow.dataset.fgaCursorGlow = '';
    glow.setAttribute('aria-hidden', 'true');

    document.body.appendChild(glow);

    FGA.mouseX = window.innerWidth / 2;
    FGA.mouseY = window.innerHeight / 2;
    FGA.glowX = FGA.mouseX;
    FGA.glowY = FGA.mouseY;

    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType === 'touch') return;

        FGA.mouseX = event.clientX;
        FGA.mouseY = event.clientY;

        glow.classList.add('is-visible');
      },
      {
        passive: true
      }
    );

    document.addEventListener('pointerleave', () => {
      glow.classList.remove('is-visible');
    });

    document.addEventListener('pointerenter', () => {
      glow.classList.add('is-visible');
    });

    const animateGlow = () => {
      FGA.glowX += (FGA.mouseX - FGA.glowX) * 0.14;
      FGA.glowY += (FGA.mouseY - FGA.glowY) * 0.14;

      glow.style.transform =
        `translate3d(${FGA.glowX}px, ${FGA.glowY}px, 0) ` +
        'translate3d(-50%, -50%, 0)';

      FGA.glowFrame = window.requestAnimationFrame(animateGlow);
    };

    animateGlow();
  };

  /**
   * Magnetic buttons and controls
   */
  const initializeMagneticElements = (root = document) => {
    if (!finePointerQuery.matches || reducedMotionQuery.matches) return;

    const selector = [
      '.fga-button',
      '.fga-product__add-button',
      '.fga-campaign__button',
      '.fga-story__button',
      '.fga-contact__submit',
      '.fga-cart__checkout',
      '.fga-3d-showcase__shop-button',
      '.fga-3d-showcase__arrow'
    ].join(',');

    root.querySelectorAll(selector).forEach((element) => {
      if (element.dataset.fgaMagneticReady === 'true') return;

      element.dataset.fgaMagneticReady = 'true';
      element.classList.add('fga-magnetic');

      element.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;

        const bounds = element.getBoundingClientRect();
        const relativeX = event.clientX - bounds.left;
        const relativeY = event.clientY - bounds.top;

        const xMovement =
          ((relativeX / bounds.width) - 0.5) * 9;

        const yMovement =
          ((relativeY / bounds.height) - 0.5) * 7;

        element.style.setProperty(
          '--fga-magnetic-x',
          `${xMovement.toFixed(2)}px`
        );

        element.style.setProperty(
          '--fga-magnetic-y',
          `${yMovement.toFixed(2)}px`
        );
      });

      element.addEventListener('pointerleave', () => {
        element.style.setProperty('--fga-magnetic-x', '0px');
        element.style.setProperty('--fga-magnetic-y', '0px');
      });
    });
  };

  /**
   * Quantity selectors
   */
  const initializeQuantitySelectors = (root = document) => {
    root.querySelectorAll('[data-fga-quantity]').forEach((quantity) => {
      if (quantity.dataset.fgaQuantityReady === 'true') return;

      quantity.dataset.fgaQuantityReady = 'true';

      const input = quantity.querySelector('input[type="number"]');
      const minusButton = quantity.querySelector(
        '[data-fga-quantity-minus]'
      );
      const plusButton = quantity.querySelector(
        '[data-fga-quantity-plus]'
      );

      if (!input) return;

      const minimum = Number.parseInt(input.min || '0', 10);
      const maximum = Number.parseInt(
        input.max || '999999',
        10
      );

      const updateValue = (newValue) => {
        const safeValue = clamp(
          Number.isFinite(newValue) ? newValue : minimum,
          minimum,
          maximum
        );

        input.value = safeValue;
        input.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      };

      minusButton?.addEventListener('click', () => {
        const currentValue =
          Number.parseInt(input.value || `${minimum}`, 10);

        updateValue(currentValue - 1);
      });

      plusButton?.addEventListener('click', () => {
        const currentValue =
          Number.parseInt(input.value || `${minimum}`, 10);

        updateValue(currentValue + 1);
      });

      input.addEventListener('change', () => {
        updateValue(Number.parseInt(input.value, 10));
      });
    });
  };

  /**
   * Generic disclosure accordions
   */
  const initializeDisclosures = (root = document) => {
    root.querySelectorAll('[data-fga-disclosure]').forEach((disclosure) => {
      if (disclosure.dataset.fgaDisclosureReady === 'true') return;

      disclosure.dataset.fgaDisclosureReady = 'true';

      const button = disclosure.querySelector(
        '[data-fga-disclosure-button]'
      );

      const content = disclosure.querySelector(
        '[data-fga-disclosure-content]'
      );

      if (!button || !content) return;

      const setDisclosureState = (open) => {
        disclosure.classList.toggle('is-open', open);
        button.setAttribute(
          'aria-expanded',
          open ? 'true' : 'false'
        );

        content.hidden = !open;
      };

      setDisclosureState(
        button.getAttribute('aria-expanded') === 'true'
      );

      button.addEventListener('click', () => {
        const nextState =
          button.getAttribute('aria-expanded') !== 'true';

        setDisclosureState(nextState);
      });
    });
  };

  /**
   * Header scroll state
   */
  const initializeHeaderScroll = () => {
    const header = document.querySelector(
      '[data-fga-header], .fga-header'
    );

    if (!header || header.dataset.fgaScrollReady === 'true') return;

    header.dataset.fgaScrollReady = 'true';

    let ticking = false;

    const updateHeader = () => {
      header.classList.toggle(
        'is-scrolled',
        window.scrollY > 18
      );

      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;

        ticking = true;
        window.requestAnimationFrame(updateHeader);
      },
      {
        passive: true
      }
    );

    updateHeader();
  };

  /**
   * External links
   */
  const initializeExternalLinks = (root = document) => {
    root.querySelectorAll('a[target="_blank"]').forEach((link) => {
      const relValues = new Set(
        (link.getAttribute('rel') || '')
          .split(/\s+/)
          .filter(Boolean)
      );

      relValues.add('noopener');
      relValues.add('noreferrer');

      link.setAttribute('rel', Array.from(relValues).join(' '));
    });
  };

  /**
   * Add-to-cart messages from native forms
   */
  const initializeProductFormFeedback = (root = document) => {
    root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.fgaFeedbackReady === 'true') return;

      form.dataset.fgaFeedbackReady = 'true';

      form.addEventListener('submit', () => {
        const submitButton = form.querySelector(
          'button[type="submit"], input[type="submit"]'
        );

        if (!submitButton) return;

        submitButton.classList.add('is-loading');

        window.setTimeout(() => {
          submitButton.classList.remove('is-loading');
        }, 1800);
      });
    });
  };

  /**
   * Initializes a page or newly loaded Shopify section
   */
  const initializeRoot = (root = document) => {
    if (
      root !== document &&
      FGA.initializedRoots.has(root)
    ) {
      return;
    }

    if (root !== document) {
      FGA.initializedRoots.add(root);
    }

    registerRevealElements(root);
    initializeMagneticElements(root);
    initializeQuantitySelectors(root);
    initializeDisclosures(root);
    initializeExternalLinks(root);
    initializeProductFormFeedback(root);
  };

  /**
   * Theme Editor support
   */
  const initializeShopifyEditorEvents = () => {
    document.addEventListener('shopify:section:load', (event) => {
      initializeRoot(event.target);

      event.target
        .querySelectorAll('.fga-reveal')
        .forEach((element) => {
          element.classList.add('is-visible');
        });
    });

    document.addEventListener('shopify:section:select', (event) => {
      const section = event.target;

      section
        .querySelectorAll('.fga-reveal')
        .forEach((element) => {
          element.classList.add('is-visible');
        });

      section.scrollIntoView({
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
        block: 'center'
      });
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;

      block.classList.add('is-visible');

      block.scrollIntoView({
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
        block: 'center'
      });
    });
  };

  /**
   * Reduced-motion changes
   */
  const handleMotionPreferenceChange = () => {
    document.documentElement.classList.toggle(
      'fga-reduced-motion',
      reducedMotionQuery.matches
    );

    if (reducedMotionQuery.matches) {
      if (FGA.glowFrame) {
        window.cancelAnimationFrame(FGA.glowFrame);
        FGA.glowFrame = null;
      }

      document
        .querySelector('[data-fga-cursor-glow]')
        ?.remove();

      document.querySelectorAll('.fga-reveal').forEach((element) => {
        element.classList.add('is-visible');
      });
    } else {
      createCursorGlow();
    }
  };

  /**
   * Main initialization
   */
  const initializeTheme = () => {
    document.documentElement.classList.toggle(
      'fga-reduced-motion',
      reducedMotionQuery.matches
    );

    initializePageReady();
    initializeRoot(document);
    initializeHeaderScroll();
    createCursorGlow();
    initializeShopifyEditorEvents();

    reducedMotionQuery.addEventListener?.(
      'change',
      handleMotionPreferenceChange
    );

    announce('Feel Good Attire storefront loaded.');
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeTheme,
      {
        once: true
      }
    );
  } else {
    initializeTheme();
  }
})();